import { EditorState, AnyNode } from '@varos/editor-core';
import { buildCoreBlocksFromEditorState } from '@varos/research-editor';
import { generated, SuggestionOperation } from '@varos/research-sdk';
import { Block } from '@varos/research-sdk';

export interface BlockDiffResult {
    operations: generated.AnyContentBlockOperation[];
    hasChanges: boolean;
}

/**
 * Compares two editor states and generates block operations for the differences
 */
export function generateBlockDiff(
    currentState: EditorState,
    previousState: EditorState,
    existingBlocks: Block[]
): BlockDiffResult {
    // Get blocks from both states
    const currentBlocks = buildCoreBlocksFromEditorState(currentState);
    const previousBlocks = buildCoreBlocksFromEditorState(previousState);

    // Create maps using node IDs for identification
    const currentNodeMap = new Map(currentState.nodes.map((node) => [node.id, node]));
    const previousNodeMap = new Map(previousState.nodes.map((node) => [node.id, node]));

    // Create maps of blocks by node ID
    const currentBlockMap = new Map(
        currentBlocks.map((block, index) => {
            const nodeId = currentState.nodes[index]?.id || `unknown-${index}`;
            return [nodeId, block];
        })
    );

    const previousBlockMap = new Map(
        previousBlocks.map((block, index) => {
            const nodeId = previousState.nodes[index]?.id || `unknown-${index}`;
            return [nodeId, block];
        })
    );

    const operations: generated.AnyContentBlockOperation[] = [];

    // Find new or modified blocks
    for (const [nodeId, currentBlock] of currentBlockMap) {
        const previousBlock = previousBlockMap.get(nodeId);

        if (!previousBlock) {
            // New block - create operation
            operations.push(currentBlock as generated.AnyContentBlockOperation);
        } else {
            // Check if block content changed
            const currentJson = JSON.stringify(currentBlock);
            const previousJson = JSON.stringify(previousBlock);

            if (currentJson !== previousJson) {
                // Modified block - update operation (need to add the block ID)
                const existingBlock = existingBlocks.find((b) => b.id === nodeId);
                const blockWithId = {
                    ...currentBlock,
                    id: existingBlock?.id || undefined,
                } as generated.AnyContentBlockOperation;
                operations.push(blockWithId);
            }
        }
    }

    // Find deleted blocks
    for (const [nodeId, previousBlock] of previousBlockMap) {
        if (!currentBlockMap.has(nodeId)) {
            // Deleted block - delete operation
            const existingBlock = existingBlocks.find((b) => b.id === nodeId);
            if (existingBlock) {
                operations.push({
                    ...previousBlock,
                    id: existingBlock.id,
                    deleted: true,
                } as generated.AnyContentBlockOperation);
            }
        }
    }

    return {
        operations,
        hasChanges: operations.length > 0,
    };
}

/**
 * Helper to apply suggestion operations to the current editor content and return
 * an EditorState suitable for replaceContent method
 */
export function applyOperationsToContent(
    currentEditorState: EditorState,
    operations: SuggestionOperation[],
    existingBlocks: Block[]
): { content: EditorState | null; hasChanges: boolean } {
    // Start with current nodes
    let newNodes = [...currentEditorState.nodes];
    let hasChanges = false;

    // Track block positions for insertions
    const nodeIds = currentEditorState.nodes.map((node) => node.id);

    operations.forEach((operation, operationIndex) => {
        // if (operation.status !== 'accepted') {
        //     return; // Only apply accepted operations
        // }

        switch (operation.kind) {
            case 'create': {
                hasChanges = true;
                const { position, block } = operation;

                // Convert BlockCreatePayload to AnyNode
                const newNode: AnyNode = convertBlockCreatePayloadToNode(block);

                if (position.kind === 'append') {
                    // Add to the end
                    newNodes.push(newNode);
                } else if (position.kind === 'before') {
                    // Insert before the anchor block
                    const anchorIndex = nodeIds.findIndex((id) => id === position.anchor);
                    if (anchorIndex < 0) {
                        throw new Error(
                            `Operation ${operationIndex}: Cannot insert before anchor "${position.anchor}" - node not found in current state. Available nodes: [${nodeIds.join(', ')}]`
                        );
                    }
                    newNodes.splice(anchorIndex, 0, newNode);
                    // Update nodeIds to reflect the new structure
                    nodeIds.splice(anchorIndex, 0, newNode.id);
                } else if (position.kind === 'after') {
                    // Insert after the anchor block
                    const anchorIndex = nodeIds.findIndex((id) => id === position.anchor);
                    if (anchorIndex < 0) {
                        throw new Error(
                            `Operation ${operationIndex}: Cannot insert after anchor "${position.anchor}" - node not found in current state. Available nodes: [${nodeIds.join(', ')}]`
                        );
                    }
                    newNodes.splice(anchorIndex + 1, 0, newNode);
                    // Update nodeIds to reflect the new structure
                    nodeIds.splice(anchorIndex + 1, 0, newNode.id);
                }
                break;
            }

            case 'update': {
                hasChanges = true;
                const nodeIndex = nodeIds.findIndex((id) => id === operation.id);
                if (nodeIndex < 0) {
                    throw new Error(
                        `Operation ${operationIndex}: Cannot update node "${operation.id}" - node not found in current state. Available nodes: [${nodeIds.join(', ')}]`
                    );
                }
                const updatedNode = convertBlockCreatePayloadToNode(operation.block);
                // Preserve the original ID
                updatedNode.id = operation.id;
                newNodes[nodeIndex] = updatedNode;
                break;
            }

            case 'delete': {
                hasChanges = true;
                const nodeIndex = nodeIds.findIndex((id) => id === operation.id);
                if (nodeIndex < 0) {
                    throw new Error(
                        `Operation ${operationIndex}: Cannot delete node "${operation.id}" - node not found in current state. Available nodes: [${nodeIds.join(', ')}]`
                    );
                }
                newNodes.splice(nodeIndex, 1);
                nodeIds.splice(nodeIndex, 1);
                break;
            }

            default:
                throw new Error(
                    `Operation ${operationIndex}: Unknown operation kind "${(operation as any).kind}". Expected: create, update, or delete.`
                );
        }
    });

    if (!hasChanges) {
        return { content: null, hasChanges: false };
    }

    // Return new EditorState
    const newEditorState: EditorState = {
        nodes: newNodes,
    };

    return { content: newEditorState, hasChanges };
}

/**
 * Convert BlockCreatePayload to AnyNode
 * This is a simplified conversion - expand as needed for all block types
 */
function convertBlockCreatePayloadToNode(block: any): AnyNode {
    const id = `node-${Math.random().toString(36).substr(2, 9)}`;

    switch (block.kind) {
        case 'text':
            return {
                id,
                kind: 'text',
                content: block.items || [],
            };
        case 'heading':
            return {
                id,
                kind: 'heading',
                level: block.level || 1,
                content: block.items || [],
            };
        case 'list_item':
            return {
                id,
                kind: 'list_item',
                style: block.style || 'bullet',
                content: block.items || [],
            };
        case 'divider':
            return {
                id,
                kind: 'divider',
            };
        case 'callout':
            return {
                id,
                kind: 'callout',
                icon: block.icon || null,
                content: [
                    {
                        kind: 'text',
                        text: block.text || '',
                        annotations: { bold: false, italic: false, underline: false },
                        href: null,
                    },
                ],
            };
        default:
            // For other types, create a custom node
            return {
                id,
                kind: 'custom',
                blockType: block.kind,
                blockId: id,
            };
    }
}
